import PropTypes from 'prop-types';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { Figures } from 'UI/components/Figures';
import { HEADER_HEIGHT, REVEAL_ANIMATION_PROPS } from 'utils/constants';
import Illustration from 'UI/components/Illustration';
import { TYPOGRAPHY_SIZE } from 'UI/components/Typography/utils/useTypography';
import { getDocumentFields } from 'utils/helper';
import { LINK_TYPE } from 'utils/constants/linkType';
import CallToAction from 'UI/components/CallToAction';
import { getSegmentText } from 'UI/components/SectionTitle/utils/get-segment-text';
import ContentfulParser from 'components/BlogCommon/Article/ContentfulParser';
import Media from 'UI/components/Media';
import useSectionProps from './utils/useSectionProps';
import styles from './styles.module.scss';

const Animated = dynamic(() => import('UI/containers/Animated'));
const Typography = dynamic(() => import('UI/components/Typography'));
const Button = dynamic(() => import('UI/components/Button'));

const PageIntroSection = (props) => {
  const {
    type,
    title,
    description,
    formattedDescription,
    subtitle,
    image,
    figuresData,
    imagesBundles,
    introSection,
    handleOnCTAClick: handleOnCTAClickCommon,
    links,
    ctaLinks,
  } = useSectionProps(props);

  // TODO: use rich text instead of splitting
  const descriptionParagraphs = description?.split('||');

  const handleOnCTAClick = ({ linkType, url }) => () => {
    if (linkType === LINK_TYPE.scrollToSection) {
      const element = document.getElementById(url);

      window.scrollBy({
        top: element.getBoundingClientRect().top - HEADER_HEIGHT - 50,
        behavior: 'smooth',
      });
    } else {
      handleOnCTAClickCommon();
    }
  };

  return (
    <section
      ref={introSection}
      className={cn(styles.pageIntroSection, styles[type])}
    >
      <div className={styles.contentWrapper}>
        <div className={styles.pageWrapper}>
          <div className={styles.pageTextContainer}>
            <Animated {...REVEAL_ANIMATION_PROPS}>
              {title && (
                <Typography
                  variant="h1"
                  size={TYPOGRAPHY_SIZE.headline46}
                  mobileSize={TYPOGRAPHY_SIZE.headline24}
                  className={styles.pageTitle}
                >
                  {getSegmentText(title)}
                </Typography>
              )}
            </Animated>
            <Animated {...REVEAL_ANIMATION_PROPS}>
              {descriptionParagraphs?.map((text, index) => (
                <Typography
                  variant="p"
                  className={styles.pageSubtitle}
                  key={`paragraph/${index}`}
                >
                  {text}
                </Typography>
              ))}
            </Animated>
            <Animated {...REVEAL_ANIMATION_PROPS}>
              {subtitle && (
                <Typography
                  variant="p"
                  className={styles.pageSubtitleSmall}
                >
                  {subtitle}
                </Typography>
              )}
            </Animated>
            <Animated {...REVEAL_ANIMATION_PROPS}>
              {formattedDescription && (
                <div
                  data-description
                  className={styles.formattedDescription}
                >
                  <ContentfulParser document={formattedDescription} />
                </div>
              )}
            </Animated>
            {links?.length > 0 && (
              <div className={styles.buttonWrapper}>
                {links?.map((link, index) => {
                  const {
                    url,
                    buttonTitle,
                    type: linkType,
                  } = getDocumentFields(link, [
                    'buttonTitle',
                    'url',
                    'type',
                  ]);

                  return (
                    <Animated
                      key={`${link.url}-${link.buttonTitle}`}
                      {...REVEAL_ANIMATION_PROPS}
                    >
                      <Button
                        onClick={url && linkType !== LINK_TYPE.scrollToSection ? null : handleOnCTAClick({ linkType, url })}
                        className={cn(styles.button, styles[`button-${index + 1}`])}
                        href={linkType !== LINK_TYPE.scrollToSection ? url : null}
                        data-button
                      >
                        {buttonTitle}
                      </Button>
                    </Animated>
                  );
                })}
              </div>
            )}
          </div>
          <Animated {...REVEAL_ANIMATION_PROPS}>
            {image.url && (
              <Illustration
                src={image.url}
                alt={image.alt}
                className={styles.pageImage}
                transparent
                priority
              />
            )}
          </Animated>
        </div>
        <Figures
          type={type}
          figuresData={figuresData}
        />
        <div className={styles.bundleImagesContainer}>
          {imagesBundles?.map((imageItem, index) => (
            <Media
              priority
              transparent
              className={cn(styles.bundleImage, styles[`bundleImage-${index + 1}`])}
              asset={imageItem}
              key={`intro-images-bundles/${imageItem.url}`}
            />
          ))}
        </div>
      </div>
      {!!ctaLinks?.length && (
        <CallToAction
          data={ctaLinks[0]}
          className={styles.callToAction}
          sectionRef={introSection}
        />
      )}
    </section>
  );
};

PageIntroSection.defaultProps = {
  introSection: null,
};

PageIntroSection.propTypes = {
  section: PropTypes.instanceOf(Object).isRequired,
  type: PropTypes.string.isRequired,
  introSection: PropTypes.instanceOf(Object),
};

export default PageIntroSection;
